/**
 * Het gesprek: de berichten op het scherm, wie er wordt aangesproken, en
 * alles wat er met de server over wordt uitgewisseld.
 *
 * Het gesprek leeft in het scherm en niet in de stap Chat, omdat de
 * leerlingen tussen de chat en het argumentenformulier heen en weer gaan en
 * het gesprek daarbij moet blijven staan. De opmaak zit in stappen/Chat.js;
 * hier staat wat er gebeurt.
 *
 * Een bericht op het scherm: { type, text, from, to }. type is 'user',
 * 'persona', 'discussieleider', 'uitleg' of 'taalcorrectie'.
 */
import { useEffect, useRef, useState } from 'react';

// Hoe lang na de opening van Socrates een van de personages ongevraagd zijn
// eerste reactie geeft. Genoeg tijd om Socrates te lezen, kort genoeg om niet
// tegen een stilte aan te kijken.
export const OPENING_NA_MS = 10000;

export function useGesprek({ api, discussion, discussieleider }) {
  const [berichten, setBerichten] = useState([]);
  // Wat er in het invoerveld staat. Hier en niet in de stap Chat, zodat een
  // half getypt bericht blijft staan als de leerlingen even naar de
  // argumenten gaan.
  const [invoer, setInvoer] = useState('');
  const [activePersona, setActivePersona] = useState(null);
  const [bezig, setBezig] = useState(false);
  // De tijdklok voor de eerste reactie van een personage. In een ref, want
  // een herrendering mag hem niet opnieuw zetten en bij het verlaten van het
  // scherm moet hij weg.
  const openingTimer = useRef(null);
  useEffect(() => () => clearTimeout(openingTimer.current), []);

  const discussionId = discussion?.discussion_id;

  // Een willekeurig personage geeft ongevraagd de eerste reactie. De backend
  // doet dit maar een keer per discussie, dus een tweede aanroep is geen
  // ramp; lukt het niet, dan blijft het bij de opening van Socrates en kunnen
  // de leerlingen zelf beginnen.
  const haalEersteReactie = async () => {
    try {
      const data = await api.eersteReactie(discussionId);
      if (!data.success || !data.data) return;
      setBerichten(prev => [...prev, { type: 'persona', text: data.data.bericht, from: data.data.persona.naam }]);
      // Het invoerveld richten op wie er net sprak, maar alleen als de
      // leerlingen zelf nog niemand hadden aangeklikt.
      setActivePersona(prev => prev || data.data.persona);
    } catch (err) { console.error('Fout:', err); }
  };

  /** De opening van Socrates, en daarna de tijdklok voor de eerste reactie. */
  const start = async () => {
    setBezig(true);
    try {
      const data = await api.openingVanSocrates(discussionId);
      if (data.success) {
        setBerichten([{ type: 'discussieleider', text: data.data.response.bericht, from: 'Socrates' }]);
        clearTimeout(openingTimer.current);
        openingTimer.current = setTimeout(haalEersteReactie, OPENING_NA_MS);
      }
    } catch (err) { console.error('Fout:', err); }
    finally { setBezig(false); }
  };

  /**
   * Het bericht uit het invoerveld, aan wie er is aangeklikt. Is het
   * tegengehouden door het taalfilter, dan staat het niet in het gesprek
   * en komt de tekst terug in het invoerveld.
   */
  const stuur = async () => {
    const tekst = invoer;
    if (!tekst.trim() || !activePersona || bezig) return;
    const eigen = { type: 'user', text: tekst, to: activePersona.naam };
    setBerichten(prev => [...prev, eigen]);
    setInvoer('');
    setBezig(true);
    try {
      let data;
      if (activePersona.id === discussieleider?.id) {
        const recent = berichten.slice(-5).map(m => m.from + ': ' + m.text).join('\n');
        const context = 'Recente berichten:\n' + recent + '\n\nLeerling vraagt: ' + tekst;
        data = await api.vraagAanSocrates(discussionId, context, tekst);
        if (data.success) {
          setBerichten(prev => [...prev, {
            type: data.data.tegengehouden ? 'taalcorrectie' : 'discussieleider',
            text: data.data.response.bericht, from: 'Socrates',
          }]);
        }
      } else {
        data = await api.berichtAanPersona(discussionId, activePersona.id, tekst);
        if (data.success) {
          setBerichten(prev => [...prev, {
            type: data.data.tegengehouden ? 'taalcorrectie' : 'persona',
            text: data.data.response.bericht, from: data.data.response.van,
          }]);
        }
      }
      // Is het bericht tegengehouden, dan heeft de server het niet in het
      // gesprek gezet. Haal het dan ook uit het scherm.
      if (data?.data?.tegengehouden) {
        setBerichten(prev => prev.filter(m => m !== eigen));
        setInvoer(tekst);
      }
    } catch (err) { console.error('Fout:', err); }
    finally { setBezig(false); }
  };

  /** Uitleg van Socrates over een stuk geselecteerde tekst. */
  const vraagUitleg = async (tekst) => {
    if (!tekst || bezig) return;
    setBezig(true);
    try {
      const data = await api.vraagUitleg(discussionId, tekst);
      if (data.success) {
        setBerichten(prev => [...prev, { type: 'uitleg', text: data.data.response.bericht, from: 'Socrates' }]);
      }
    } catch (err) { console.error('Fout bij uitleg:', err); }
    finally { setBezig(false); }
  };

  return { berichten, invoer, setInvoer, activePersona, setActivePersona, bezig, start, stuur, vraagUitleg };
}
